import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export interface ActivityLog {
  id: string;
  user_id: string;
  user_name: string;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  metadata: Record<string, any>;
  created_at: string;
}

export interface ActivityLogFilters {
  userId?: string;
  entityType?: string;
  period?: 'today' | 'week' | 'month' | 'all';
  limit?: number;
}

export const useActivityLogs = () => {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLogs = async (filters?: ActivityLogFilters) => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('activity_logs')
        .select('*')
        .order('created_at', { ascending: false });

      // Filtro por usuário
      if (filters?.userId) {
        query = query.eq('user_id', filters.userId);
      }

      // Filtro por tipo de entidade
      if (filters?.entityType) {
        query = query.eq('entity_type', filters.entityType);
      }

      // Filtro por período
      if (filters?.period && filters.period !== 'all') {
        const now = new Date();
        let startDate: Date;

        switch (filters.period) {
          case 'today':
            startDate = new Date(now.setHours(0, 0, 0, 0));
            break;
          case 'week':
            startDate = new Date(now.setDate(now.getDate() - 7));
            break;
          case 'month':
            startDate = new Date(now.setDate(now.getDate() - 30));
            break;
          default:
            startDate = new Date(0);
        }

        query = query.gte('created_at', startDate.toISOString());
      }

      // Limite de resultados
      if (filters?.limit) {
        query = query.limit(filters.limit);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      setLogs(data || []);
    } catch (err: any) {
      console.error('Erro ao buscar logs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const logActivity = async (
    action: string,
    entityType?: string,
    entityId?: string,
    metadata?: Record<string, any>
  ) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', user.id)
        .maybeSingle();

      const userName = profile?.full_name || user.email || 'Usuário';

      const { error: insertError } = await supabase
        .from('activity_logs')
        .insert({
          user_id: user.id,
          user_name: userName,
          action,
          entity_type: entityType || null,
          entity_id: entityId || null,
          metadata: metadata || {}
        });

      if (insertError) throw insertError;
    } catch (err: any) {
      console.error('Erro ao registrar atividade:', err);
    }
  };

  const getRecentUserActivities = async (userId: string, limit: number = 3) => {
    try {
      const { data, error } = await supabase
        .from('activity_logs')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return data || [];
    } catch (err: any) {
      console.error('Erro ao buscar atividades recentes:', err);
      return [];
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  return {
    logs,
    loading,
    error,
    fetchLogs,
    logActivity,
    getRecentUserActivities
  };
};